package com.arena_web.ui.components.events

import androidx.compose.runtime.Composable
import com.arena_web.data.dto.EventParticipantDto
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.overlay.BottomSheet
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.toAnimation
import org.jetbrains.compose.web.css.*

private const val MAX_CARDS_VISIBLE = 6

@Composable
fun StartEventConfirmDialog(
    isOpen: Boolean,
    eventTitle: String,
    isStarting: Boolean,
    sportName: String = "football",
    pendingParticipants: List<EventParticipantDto> = emptyList(),
    teamlessParticipants: List<EventParticipantDto> = emptyList(),
    onConfirm: () -> Unit,
    onCancel: () -> Unit,
) {
    if (!isOpen) return

    // ── Mobile: same content hosted in a bottom sheet — the phone counterpart
    // of the centered popup (matches CreateTeamPopup's responsive branch). ──
    if (isMobile()) {
        BottomSheet(onDismiss = onCancel, contentGap = 20) {
            StartEventConfirmBody(
                eventTitle = eventTitle,
                isStarting = isStarting,
                sportName = sportName,
                pendingParticipants = pendingParticipants,
                teamlessParticipants = teamlessParticipants,
                // The sheet's drag handle already tops the panel — the halo
                // glyph would push the content down for no benefit.
                showIcon = false,
                onConfirm = onConfirm,
                onCancel = onCancel,
            )
        }
        return
    }

    Box(
        modifier = Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .zIndex(1000)
            .backgroundColor(Color("rgba(0, 0, 0, 0.6)"))
            .onClick { if (!isStarting) onCancel() }
            .animation(
                PopupOverlayFadeIn.toAnimation(
                    duration = Constants.ANIMATION_FAST.ms,
                    timingFunction = AnimationTimingFunction.EaseOut
                )
            ),
        contentAlignment = Alignment.Center
    ) {
        Column(
            modifier = Modifier
                .sMaxWidth(520)
                .fillMaxWidth()
                .sMargin(16)
                .sBorderRadius(26)
                .sPadding(28, 26)
                .sGap(20)
                .onClick { it.stopPropagation() }
                .animation(
                    PopupSlideUp.toAnimation(
                        duration = Constants.ANIMATION_NORMAL.ms,
                        timingFunction = AnimationTimingFunction.EaseOut
                    )
                )
                .styleModifier {
                    property("background-color", "#09142D")
                    property("box-shadow", "0 24px 64px rgba(0, 0, 0, 0.5)")
                    property("border", "0.5px solid rgba(255, 255, 255, 0.06)")
                    property("max-height", "85vh")
                    property("overflow-y", "auto")
                }
        ) {
            StartEventConfirmBody(
                eventTitle = eventTitle,
                isStarting = isStarting,
                sportName = sportName,
                pendingParticipants = pendingParticipants,
                teamlessParticipants = teamlessParticipants,
                onConfirm = onConfirm,
                onCancel = onCancel,
            )
        }
    }
}

// Shared dialog body — emitted as siblings so the host (popup Column with
// sGap(20) / BottomSheet with contentGap 20) controls the section spacing.
@Composable
private fun StartEventConfirmBody(
    eventTitle: String,
    isStarting: Boolean,
    sportName: String,
    pendingParticipants: List<EventParticipantDto>,
    teamlessParticipants: List<EventParticipantDto>,
    onConfirm: () -> Unit,
    onCancel: () -> Unit,
    showIcon: Boolean = true,
) {
    val t = LocalAppStrings.current
    Column(
        modifier = Modifier.fillMaxWidth().sGap(12),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        // Brand-blue play glyph with a soft radial halo — no hard
        // button background or border, just a glow that fades out
        // so the icon reads as decoration, not a tappable target.
        if (showIcon) Box(
            modifier = Modifier
                .sSize(72)
                .styleModifier {
                    property(
                        "background",
                        "radial-gradient(circle, rgba(0, 97, 255, 0.28) 0%, rgba(0, 97, 255, 0.10) 45%, rgba(0, 97, 255, 0) 70%)"
                    )
                },
            contentAlignment = Alignment.Center
        ) {
            Box(
                modifier = Modifier
                    .sSize(36)
                    .styleModifier {
                        property("background-color", "#0061FF")
                        property("mask-image", "url('${AppIcons.Play}')")
                        property("-webkit-mask-image", "url('${AppIcons.Play}')")
                        property("mask-size", "contain")
                        property("-webkit-mask-size", "contain")
                        property("mask-position", "center")
                        property("-webkit-mask-position", "center")
                        property("mask-repeat", "no-repeat")
                        property("-webkit-mask-repeat", "no-repeat")
                        property("filter", "drop-shadow(0 0 12px rgba(0, 97, 255, 0.55))")
                    }
            )
        }
        SpanText(
            t.eventStartEventQuestion,
            modifier = Modifier
                .sFontSize(20)
                .fontWeight(FontWeight.SemiBold)
                .color(AppColors.Text.Secondary)
                .styleModifier {
                    property("font-family", "Urbanist, sans-serif")
                    property("text-align", "center")
                }
        )
    }

    Column(
        modifier = Modifier.fillMaxWidth().sGap(6),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        SpanText(
            t.eventWillGoLive(eventTitle),
            modifier = Modifier
                .sFontSize(15)
                .color(Color("#BED6F5"))
                .styleModifier {
                    property("font-family", "Urbanist, sans-serif")
                    property("line-height", "1.5")
                    property("text-align", "center")
                }
        )
        SpanText(
            t.eventStartNotice,
            modifier = Modifier
                .sFontSize(13)
                .color(Color("#99B1BF"))
                .styleModifier {
                    property("font-family", "Urbanist, sans-serif")
                    property("line-height", "1.5")
                    property("text-align", "center")
                }
        )
    }

    if (pendingParticipants.isNotEmpty()) {
        WarningSection(
            accentColor = "#FFB800",
            title = if (pendingParticipants.size == 1) {
                t.eventPendingRequestOne(pendingParticipants.size)
            } else {
                t.eventPendingRequestsMany(pendingParticipants.size)
            },
            subtitle = t.eventPendingRequestsSubtitle,
            participants = pendingParticipants,
            sportName = sportName,
        )
    }

    if (teamlessParticipants.isNotEmpty()) {
        WarningSection(
            accentColor = "#E88908",
            title = if (teamlessParticipants.size == 1) {
                t.eventPlayersWithoutTeamOne(teamlessParticipants.size)
            } else {
                t.eventPlayersWithoutTeamMany(teamlessParticipants.size)
            },
            subtitle = t.eventTeamlessSubtitle,
            participants = teamlessParticipants,
            sportName = sportName,
        )
    }

    Row(
        modifier = Modifier.fillMaxWidth().sGap(12),
        horizontalArrangement = Arrangement.Center,
        verticalAlignment = Alignment.CenterVertically
    ) {
        DialogPillButton(
            text = t.commonCancel,
            onClick = { if (!isStarting) onCancel() },
            background = AppGradients.InteractiveSecondary,
            textColor = "#BED6F5",
            enabled = !isStarting,
        )
        DialogPillButton(
            text = if (isStarting) t.eventStarting else t.eventStartAnyway,
            onClick = { if (!isStarting) onConfirm() },
            background = "#0061FF",
            textColor = "#FFFFFF",
            enabled = !isStarting,
        )
    }
}

@Composable
private fun WarningSection(
    accentColor: String,
    title: String,
    subtitle: String,
    participants: List<EventParticipantDto>,
    sportName: String,
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .sBorderRadius(14)
            .sPadding(12, 14)
            .sGap(12)
            .styleModifier {
                property("background-color", "rgba(255, 255, 255, 0.03)")
                property("border", "1px solid ${accentColor}59") // ~35% alpha
            }
    ) {
        Column(modifier = Modifier.fillMaxWidth().sGap(2)) {
            SpanText(
                title,
                modifier = Modifier
                    .sFontSize(14)
                    .fontWeight(FontWeight.SemiBold)
                    .color(Color(accentColor))
                    .styleModifier { property("font-family", "Urbanist, sans-serif") }
            )
            SpanText(
                subtitle,
                modifier = Modifier
                    .sFontSize(12)
                    .color(Color("#99B1BF"))
                    .styleModifier {
                        property("font-family", "Urbanist, sans-serif")
                        property("line-height", "1.4")
                    }
            )
        }

        // Phone sheet: one horizontally-scrollable strip (no wrap) so every
        // flagged player stays reachable without growing the sheet; desktop
        // keeps the wrapping grid capped by the +N overflow card.
        val horizontal = isMobile()
        val visible = if (horizontal) participants else participants.take(MAX_CARDS_VISIBLE)
        val overflow = participants.size - visible.size

        // PlayerCard at Arena size (94×147) matches the cards shown in the
        // event registration grid, so the admin recognises exactly which
        // players are flagged.
        Box(
            modifier = Modifier.fillMaxWidth().styleModifier {
                property("display", "flex")
                if (horizontal) {
                    property("flex-wrap", "nowrap")
                    property("overflow-x", "auto")
                    property("scrollbar-width", "none")
                } else {
                    property("flex-wrap", "wrap")
                }
                property("gap", "${s(12)} ${s(12)}")
            }
        ) {
            visible.forEach { p ->
                val lastName = (p.fullName?.split(" ")?.lastOrNull() ?: p.username).uppercase()
                // flex-shrink 0 keeps the fixed-size cards from squeezing in
                // the nowrap strip.
                Box(modifier = Modifier.flexShrink(0)) {
                    if (p.cardAssetsBasePath != null) {
                        PlayerCard(
                            playerName = lastName,
                            rating = p.overallRating.toInt(),
                            position = p.position ?: "",
                            sport = sportName,
                            size = PlayerCardSize.Arena,
                            avatarUrl = p.cardAvatarUrl ?: AppIcons.EmptyAvatar,
                            cardAssetsBasePath = p.cardAssetsBasePath,
                            cardTextColor = p.cardTextColor,
                        )
                    } else {
                        PlayerCard(
                            playerName = lastName,
                            sport = sportName,
                            size = PlayerCardSize.Arena,
                            avatarUrl = p.avatarUrl ?: AppIcons.EmptyAvatar,
                            hideRating = true,
                        )
                    }
                }
            }
            if (overflow > 0) {
                OverflowCard(count = overflow)
            }
        }
    }
}

@Composable
private fun OverflowCard(count: Int) {
    // Card-shaped slot matching PlayerCardSize.Arena (94×147) so it sits
    // in the same flex flow as the player cards without breaking alignment.
    Box(
        modifier = Modifier
            .sWidth(94)
            .sHeight(147)
            .sBorderRadius(10)
            .styleModifier {
                property("background-color", "rgba(255, 255, 255, 0.04)")
                property("border", "0.5px dashed rgba(255, 255, 255, 0.18)")
                property("display", "inline-flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("box-sizing", "border-box")
            },
        contentAlignment = Alignment.Center
    ) {
        SpanText(
            "+$count",
            modifier = Modifier
                .sFontSize(18)
                .fontWeight(FontWeight.SemiBold)
                .color(Color("#BED6F5"))
                .styleModifier { property("font-family", "Urbanist, sans-serif") }
        )
    }
}
